/**
 * Named `-d` (not `.d.ts`) on purpose: `defineExperiment` is executable at
 * runtime, and real `.d.ts` files are type-only and get erased at compile
 * time, so it can't live in one. This file is the type-level counterpart to
 * `experiments.ts` — read it like a declaration file, just know it isn't one.
 */

/**
 * Definition of a single experiment. `variants` is a tuple of the possible
 * resolved values and `default` is constrained to one of those variants —
 * see {@link defineExperiment}.
 */
export interface ExperimentConfig<
  TFlag extends string = string,
  TVariants extends readonly string[] = readonly string[]
> {
  /** The PostHog flag key fire-pit resolves this experiment from. */
  flag: TFlag
  /** All possible variants this experiment can resolve to. */
  variants: TVariants
  /** Value used when `flag` is missing from `config.experiments` (experiment off/unresolved). */
  default: TVariants[number]
}

/**
 * Identity function used purely to anchor generic inference on `flag` and
 * `variants`, so `default` is type-checked against that same experiment's
 * variants without needing an explicit type annotation at each call site.
 */
export function defineExperiment<const TFlag extends string, const TVariants extends readonly string[]>(
  experiment: ExperimentConfig<TFlag, TVariants>
): ExperimentConfig<TFlag, TVariants> {
  return experiment
}

/** Union of all experiment configs in a registry (e.g. `typeof Experiments`). */
export type AnyExperimentOf<T extends Record<string, ExperimentConfig>> = T[keyof T]

/** The experiment config in `T` whose `flag` matches `TFlag`. */
export type ExperimentForFlag<
  T extends Record<string, ExperimentConfig>,
  TFlag extends AnyExperimentOf<T>['flag']
> = Extract<AnyExperimentOf<T>, { flag: TFlag }>
